import {
    ActionType,
    DataExportId,
    DataExportStatus,
    formatDateNoTime,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import { inferProcedureOutput } from "@trpc/server";
import { CSVDownload } from "components/csv-download";
import { Content, PageHeader, Stage } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { CrmTable } from "components/table/crm-table";
import { CrmTableColumn } from "components/table/types";
import { Badge } from "components/ui/badge";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useIsAllowed } from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import { allowedRegionsForAction } from "util/permissions";
import {
    downloadDataExportFile,
    isRecentDataExportInProgress,
    recentDataExportRefetchInterval,
} from "./data-export-status";

type DataExportRow = inferProcedureOutput<AppRouter["dataExport"]["list"]>[number];

const StatusBadge: React.FC<{ status: DataExportStatus }> = ({ status }) => {
    switch (status) {
        case DataExportStatus.Pending:
            return <Badge value="Pending" type="info" />;
        case DataExportStatus.Processing:
            return <Badge value="Processing" type="info" />;
        case DataExportStatus.Completed:
            return <Badge value="Completed" type="success" />;
        case DataExportStatus.Failed:
            return <Badge value="Failed" type="error" />;
        default: {
            const exhaustiveCheck: never = status;
            throw new Error(`Unhandled status: ${exhaustiveCheck}`);
        }
    }
};

const DownloadButton: React.FC<{ dataExport: DataExportRow }> = ({ dataExport }) => {
    if (dataExport.status !== DataExportStatus.Completed || !dataExport.fileId) {
        return null;
    }

    const fileId = dataExport.fileId;
    return (
        <PrimaryButton onClick={() => downloadDataExportFile(fileId)}>
            Download
        </PrimaryButton>
    );
};

const ExportProgressWatcher: React.FC<{
    exportId: DataExportId;
    onFinished: () => void;
}> = ({ exportId, onFinished }) => {
    const trpcClient = trpc.useTRPC();
    const statusQuery = useQuery({
        ...trpcClient.dataExport.getById.queryOptions({ id: exportId }),
        refetchInterval: query =>
            recentDataExportRefetchInterval(
                query.state.data ? [query.state.data] : [],
                new Date(),
            ),
    });
    const dataExport = statusQuery.data;
    const finished =
        !!dataExport && !isRecentDataExportInProgress(dataExport, new Date());
    useEffect(() => {
        if (finished) {
            onFinished();
        }
    }, [finished, onFinished]);
    return null;
};

export const ExportsList: React.FC = () => {
    const csvDownloadEnabled = useIsAllowed(ActionType.ExportCsv);
    const [csvExportPanelOpen, setCsvExportPanelOpen] = useState(false);
    const regionState = useSelector((state: ApplicationState) => state.region);
    const region = regionState.regions.find(r => r.key === regionState.selectedRegion);
    const canAccessMultipleRegions = useSelector(
        (state: ApplicationState) =>
            allowedRegionsForAction(ActionType.ExportCsv, state.permissions).length > 1,
    );

    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.dataExport.list.queryOptions(),
    });
    const refetchList = query.refetch;
    const refreshList = useCallback(() => {
        void refetchList();
    }, [refetchList]);
    const now = new Date();
    const exportsInProgress = (query.data ?? []).filter(dataExport =>
        isRecentDataExportInProgress(dataExport, now),
    );

    if (!csvDownloadEnabled) {
        return null;
    }

    if (query.isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner />
            </div>
        );
    }

    if (query.isError) {
        return (
            <div className="text-red-600">
                Error loading exports: {query.error?.message}
            </div>
        );
    }

    const columns: CrmTableColumn<DataExportRow>[] = [
        {
            key: "createdAt",
            header: "Date",
            renderCell: ({ record }) => (
                <span>{formatDateNoTime(record?.createdAt)}</span>
            ),
        },
        {
            key: "description",
            header: "Export",
            renderCell: ({ record }) => <span>{record?.description || "-"}</span>,
        },
        {
            key: "requestedByName",
            header: "Created By",
            renderCell: ({ record }) => <span>{record?.requestedByName || "-"}</span>,
        },
        {
            key: "status",
            header: "Status",
            renderCell: ({ record }) =>
                record ? <StatusBadge status={record.status} /> : null,
        },
        {
            key: "completedAt",
            header: "Completed",
            renderCell: ({ record }) => (
                <span>
                    {record?.completedAt ? formatDateNoTime(record.completedAt) : "-"}
                </span>
            ),
        },
    ];

    const exports = query.data || [];

    const handleDismiss = () => {
        setCsvExportPanelOpen(false);
        query.refetch();
    };

    return (
        <Stage>
            {exportsInProgress.map(dataExport => (
                <ExportProgressWatcher
                    key={dataExport.id}
                    exportId={dataExport.id}
                    onFinished={refreshList}
                />
            ))}
            <PageHeader title="CSV Exports" />
            <div className="mb-4 px-4">
                <PrimaryButton onClick={() => setCsvExportPanelOpen(true)}>
                    Create CSV Export
                </PrimaryButton>
            </div>
            <Content>
                {exports.length === 0 ? (
                    <div className="text-gray-500 py-8 text-center">
                        No exports found. Export data from the CSV download option to see
                        them here.
                    </div>
                ) : (
                    <CrmTable
                        data={exports}
                        columns={columns}
                        special={({ record }) =>
                            record ? <DownloadButton dataExport={record} /> : null
                        }
                        initialSort={[{ id: "createdAt", desc: true }]}
                    />
                )}
            </Content>

            {csvExportPanelOpen && region && (
                <CSVDownload
                    regionName={region.name || ""}
                    regionId={region.id}
                    canAccessMultipleRegions={canAccessMultipleRegions}
                    onDismiss={handleDismiss}
                />
            )}
        </Stage>
    );
};
